import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, compact } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildMMYSubmodelTypeAmountsPath,
  InventoryAggregateEntities,
  InventoryAggregateModel,
} from 'client/data/models/inventory-aggregate';
import { buildAllPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { InventoryModel, InventoryPaths } from 'client/data/models/inventory';
import {
  buildUsedTmvPricingPath,
  getUsedBaseStylePath,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { getUsedVehiclePriceRange } from 'site-modules/shared/utils/core-page/pricing';
import { getPriceRangeString } from 'site-modules/shared/utils/price-utils';
import { getPlural } from 'client/utils/plural';
import { getMediaImageUrl, SOURCES } from 'client/utils/image-helpers';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { srpLinkBuilderWithParamsConversion } from 'site-modules/shared/utils/srp-link-constructor';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { Link } from 'site-modules/shared/components/link/link';
import { Image } from 'site-modules/shared/components/image/image';

import './srp-photo-box.scss';

const MAX_PHOTO_COUNT = 4;

export function SrpPhotoBoxUI({
  className = null,
  vehicle: {
    make: { slug: makeSlug },
    model: { slug: modelSlug },
    submodels: { slug: submodelSlug },
    year,
  },
  submodelAmounts = null,
  usedTmv = null,
  validPhotos = null,
  galleryPhotos = null,
  buttonsColor = 'primary-b',
  hideBuyOnline = false,
}) {
  if (!(submodelAmounts && validPhotos)) {
    return null;
  }
  const vehiclePhotos = validPhotos && compact(validPhotos.concat(galleryPhotos));
  const { inventoryCount: totalInventoryCount } = submodelAmounts;

  const { min, max } = getUsedVehiclePriceRange({ submodelAmounts, submodelSlug, usedTmv });

  const srpLink = getCoreUrl({ makeSlug, modelSlug, year });
  const buttonLinks = compact([
    !hideBuyOnline && {
      to: srpLinkBuilderWithParamsConversion(
        {
          make: makeSlug,
          model: modelSlug,
          year,
        },
        true
      ),
      trackingId: 'visit_buy_online_channel',
      label: 'See Cars for Online Purchase',
    },
    {
      to: srpLink,
      trackingId: 'for_sale_button_click',
      label: 'See All for Sale',
    },
  ]);

  return (
    <div className={classnames('srp-photo-box px-1', className)} data-tracking-parent="srp_photo_box">
      <div className="rounded-8 bg-cool-gray-90 pt-1_5 pb-0_5">
        <Link
          to={srpLink}
          tabIndex={-1}
          className="d-block mb-1 text-decoration-none"
          data-tracking-id="vin_photo_click"
        >
          <Row className="photos-row mb-0_5">
            {vehiclePhotos.slice(0, MAX_PHOTO_COUNT).map((photoUrl, photoIndex) => (
              <Col xs={6} key={photoUrl} className="mb-1">
                <AspectRatioContainer aspectRatio="3:2">
                  <Image
                    className="rounded-8"
                    imageId={photoUrl}
                    source={SOURCES.MEDIA}
                    lazy
                    errorImage={vehiclePhotos[MAX_PHOTO_COUNT + photoIndex]}
                  />
                </AspectRatioContainer>
              </Col>
            ))}
          </Row>
          {totalInventoryCount ? (
            <Fragment>
              <div className="heading-4 text-center">{getPriceRangeString(min, max)}</div>
              <div className="text-center">
                {totalInventoryCount} {getPlural('listing', totalInventoryCount)}
              </div>
            </Fragment>
          ) : (
            <div className="heading-4 text-center">Not available nearby</div>
          )}
          <span className="visually-hidden">&nbsp;- See All for Sale</span>
        </Link>
        <div className="px-1">
          {buttonLinks.map(({ to, trackingId, label }) => (
            <Button
              key={label}
              tag={Link}
              color={buttonsColor}
              size="lg"
              to={to}
              className="w-100 py-0_75 px-0_25 mb-1 text-transform-none fw-normal"
              data-tracking-id={trackingId}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

SrpPhotoBoxUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  usedTmv: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  className: PropTypes.string,
  submodelAmounts: InventoryAggregateEntities.MMYSubmodelTypeAmounts,
  validPhotos: PropTypes.arrayOf(PropTypes.string),
  galleryPhotos: PropTypes.arrayOf(PropTypes.string),
  buttonsColor: PropTypes.string,
  hideBuyOnline: PropTypes.bool,
};

const SrpPhotoBoxWrapper = connectToModel(SrpPhotoBoxUI, {
  galleryPhotos: bindToPath(
    ({ params, vehicle, validPhotos, isMobile, hasDesktopFallback }) =>
      (hasDesktopFallback || isMobile) &&
      !!validPhotos &&
      validPhotos.length < MAX_PHOTO_COUNT &&
      buildAllPhotosPath({
        ...params,
        pageNum: 1,
        submodel: vehicle.submodels.name,
        imageSize: 175,
      }),
    PhotoflipperModel,
    photos => get(photos, 'data', []).map(({ src }) => getMediaImageUrl(src))
  ),
  usedTmv: bindToPath(
    ({ usedBaseStyle, submodelAmounts, vehicle }) =>
      usedBaseStyle &&
      submodelAmounts &&
      !get(submodelAmounts, `priceRanges["${vehicle.submodels.slug}"]`) &&
      buildUsedTmvPricingPath(get(usedBaseStyle, 'usedDefaultStyle.id')),
    VehicleModel,
    data => ({
      min: get(data, 'ROUGH.Current.totalWithOptions.usedTmvRetail', 0),
      max: get(data, 'OUTSTANDING.Current.totalWithOptions.usedTmvRetail', 0),
    })
  ),
});

export const SrpPhotoBox = connectToModel(SrpPhotoBoxWrapper, {
  submodelAmounts: bindToPath(({ params }) => buildMMYSubmodelTypeAmountsPath(params), InventoryAggregateModel),
  usedBaseStyle: bindToPath(({ params }) => getUsedBaseStylePath(params), VehicleModel),
  validPhotos: bindToPath(
    ({ vehicle }) =>
      InventoryPaths.buildMMYValidPhotosPath({
        makeName: vehicle.make.name,
        modelName: vehicle.model.name,
        year: vehicle.year,
      }),
    InventoryModel
  ),
});
